import { BigNumber } from 'bignumber.js';
import formatDecimal, {
  isValidDecimal,
  parseFormattedFloat,
  parseInputNumber,
  removeTrailingZeroes,
} from '@paradigm/utils/formatDecimal';
import { getProduct } from '#/products/domain';
import { DrfqLeg } from '#/unified-rfqs/entities/drfq/domain';
import { hasNonIntegerRatio } from '#/unified-rfqs/ui/rfqs/legs-functions';
import { Side } from '#/unified-rfqs/entities/shared';
import { roundPrecisionBySide } from '#/unified-rfqs/utils/rfq-price-functions';
import { TwoWayValue, NullableTwoWay } from '../form-reducer';

interface PriceViewArgs {
  readonly price: TwoWayValue;
  readonly legPricingEnabled: boolean;
  readonly legsPrice: readonly NullableTwoWay[];
  readonly legs: readonly DrfqLeg[];
  readonly productCode?: string;
}

export default function priceView(args: PriceViewArgs) {
  const precision = processPricePrecision(args);
  const value = processCustomLegsPrice(args, precision);
  return { precision, value };
}

function processPricePrecision(args: PriceViewArgs) {
  const { legPricingEnabled, productCode, legs } = args;
  const { pricePrecision, ratioPrecision } = getProduct(productCode ?? '');
  if (hasNonIntegerRatio(legs)) {
    return pricePrecision + 1;
  }
  if (!legPricingEnabled) return pricePrecision;
  return Math.max(pricePrecision, ratioPrecision);
}

function processCustomLegsPrice(
  args: PriceViewArgs,
  precision: number,
): TwoWayValue {
  const { legPricingEnabled, price, legs, legsPrice } = args;
  if (!legPricingEnabled)
    return [
      formatPrice(price[0], 'BUY', precision),
      formatPrice(price[1], 'SELL', precision),
    ];

  let bid = BigNumber(0);
  let ask = BigNumber(0);
  let hasBidPrices = false;
  let hasAskPrices = false;

  for (const [index, leg] of legs.entries()) {
    const legPrices = legsPrice[index];
    const isBuyLeg = leg.side === 'BUY';
    const isSellLeg = leg.side === 'SELL';

    const bidPrice = legPrices?.[isSellLeg ? 1 : 0] ?? '';
    if (bidPrice !== '') hasBidPrices = true;
    bid = bid.plus(normalizePrice(bidPrice, leg));

    const askPrice = legPrices?.[isBuyLeg ? 1 : 0] ?? '';
    if (askPrice !== '') hasAskPrices = true;
    ask = ask.plus(normalizePrice(askPrice, leg));
  }

  const bidPrice = hasBidPrices
    ? formatPrice(bid.toString(), 'BUY', precision)
    : '';
  const askPrice = hasAskPrices
    ? formatPrice(ask.toString(), 'SELL', precision)
    : '';

  return [bidPrice, askPrice];
}
const formatPrice = (decimal: string, side: Side, precision: number) => {
  if (!isValidDecimal(decimal)) return '';
  const bigNum = BigNumber(parseFormattedFloat(decimal));

  const roundedPrice = roundPrecisionBySide(bigNum.toString(), side, precision);

  return formatDecimal(removeTrailingZeroes(roundedPrice));
};

function normalizePrice(price: string, { side, ratio }: DrfqLeg) {
  const isBuyLeg = side === 'BUY';
  const parsedRatio = parseFormattedFloat(ratio) * (isBuyLeg ? 1 : -1);
  const inputPriceParsed = parseInputNumber(price);
  if (inputPriceParsed === null) return 0;

  return BigNumber(inputPriceParsed).times(parsedRatio);
}
